import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * snak ENTEGRASYONU (M17) — restoranlar, sözleşme koşulları, gelen ekstreler.
 *
 * ⚠⚠ BU AKIŞ BİLEREK OTOMATİK: snak her ayın 1'inde ekstreyi gönderir,
 * muhasebe kapılar tutarsa faturayı KESER ve e-postayla İLETİR. Kapılardan
 * biri tutmazsa fatura kesilmez, ekstre BEKLETİLİR ve sebep burada görünür.
 * Bu ekran bekletilenleri görmenin ve düzeltmenin yeridir.
 *
 * ⚠ SÖZLEŞME BURADADIR, snak'TA DEĞİL: restoran sahibi kendi snak sunucusunda
 * yöneticidir ve oradaki ayarları değiştirebilir. Faturanın ücret satırı bu
 * ekrandaki koşuldan gelir; snak'ın bildirdiği rakam yalnız karşılaştırılır.
 */
export type SnakBillingMode = 'commission' | 'flat' | 'hybrid';
export type SnakVatMode = 'excluded' | 'included';
export type SnakStatementStatus = 'INVOICED' | 'HELD' | 'REJECTED';

export const SNAK_BILLING_MODE_LABELS: Record<SnakBillingMode, string> = {
  flat: 'Sabit aylık ücret',
  commission: 'Yalnız komisyon',
  hybrid: 'Ücret + komisyon',
};

export type SnakFeeBilling = 'ADVANCE' | 'ARREARS';

/**
 * Aylık ücretin faturalandığı ay. ⚠ Komisyon HER ZAMAN sonradandır: siparişler
 * ancak ay bitince belli olur.
 */
export const SNAK_FEE_BILLING_LABELS: Record<SnakFeeBilling, string> = {
  ADVANCE: 'Peşin — ayın 1’inde o ayın ücreti',
  ARREARS: 'Sonradan — ay bitince geçen ayın ücreti',
};

export const SNAK_VAT_MODE_LABELS: Record<SnakVatMode, string> = {
  excluded: 'Komisyon KDV hariç hesaplanır',
  included: 'Komisyon KDV dahil hesaplanır',
};

export const SNAK_STATEMENT_STATUS_LABELS: Record<SnakStatementStatus, string> = {
  INVOICED: 'Fatura kesildi',
  HELD: 'Bekletildi — fatura KESİLMEDİ',
  REJECTED: 'Reddedildi',
};

export interface SnakTerms {
  id: string;
  tenantId: string;
  effectiveFrom: string;
  billingMode: SnakBillingMode;
  monthlyFeeNet: string;
  commissionPercent: string;
  vatMode: SnakVatMode;
  /** Liste fiyatı (KDV hariç) — ücretten büyükse fark faturada belge indirimi olur. */
  listPriceNet: string | null;
  createdAt: string;
}

export interface SnakTenant {
  id: string;
  tenantKey: string;
  displayName: string;
  contactId: string;
  enabled: boolean;
  feeItemId: string | null;
  commissionItemId: string | null;
  paymentTermDays: number;
  feeBilling: SnakFeeBilling;
  secretRotatedAt: string | null;
  lastStatementAt: string | null;
  /** Döndürmeden sonra eski sır snak sunucusu güncellenene kadar geçerli. */
  hasPreviousSecret: boolean;
  terms: SnakTerms[];
}

export interface SnakHoldReason {
  code: string;
  detail: string | null;
}

export interface SnakStatementRow {
  id: string;
  tenantId: string;
  tenantKey: string;
  displayName: string;
  receivedAt: string;
  regularization: boolean;
  status: SnakStatementStatus;
  periodLabel: string;
  statementRef: string;
  holdReasons: SnakHoldReason[];
  invoiceId: string | null;
  invoiceNumber: string | null;
  issueDate: string | null;
  totalInclVat: string | null;
  prepaidAmount: string | null;
  /** SUNUCU hesaplar (brüt − önceden alınan) — panel tutar çıkarmaz. */
  payableAmount: string | null;
  delivered: boolean;
  deliveryError: string | null;
}

const KEY = ['snak'] as const;

export function useSnakTenants() {
  return useQuery({
    queryKey: [...KEY, 'tenants'],
    queryFn: () => api.get<SnakTenant[]>('/integrations/snak/tenants'),
  });
}

export function useSnakStatements(status?: SnakStatementStatus) {
  return useQuery({
    queryKey: [...KEY, 'statements', status ?? 'all'],
    queryFn: () =>
      api.get<SnakStatementRow[]>(
        `/integrations/snak/statements${status ? `?status=${status}` : ''}`,
      ),
  });
}

function useSnakInvalidator() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
    void qc.invalidateQueries({ queryKey: ['compliance'] });
    void qc.invalidateQueries({ queryKey: ['invoices'] });
  };
}

/** ⚠ Sır YALNIZ bu yanıtta döner — kaybedilirse döndürmek gerekir. */
export function useCreateSnakTenant() {
  const invalidate = useSnakInvalidator();
  return useMutation({
    mutationFn: (input: {
      tenantKey: string;
      displayName: string;
      contactId: string;
      feeItemId?: string | null;
      commissionItemId?: string | null;
      paymentTermDays?: number;
      feeBilling?: SnakFeeBilling;
    }) => api.post<{ tenant: SnakTenant; secret: string }>('/integrations/snak/tenants', input),
    onSuccess: invalidate,
  });
}

export function useUpdateSnakTenant() {
  const invalidate = useSnakInvalidator();
  return useMutation({
    mutationFn: (input: {
      id: string;
      displayName?: string;
      contactId?: string;
      enabled?: boolean;
      feeItemId?: string | null;
      commissionItemId?: string | null;
      paymentTermDays?: number;
      feeBilling?: SnakFeeBilling;
    }) => {
      const { id, ...body } = input;
      return api.patch<SnakTenant>(`/integrations/snak/tenants/${id}`, body);
    },
    onSuccess: invalidate,
  });
}

/**
 * Yanlış açılmış restoran kaydını siler (yanlış cari, rastgele anahtar — ikisi
 * sonradan değiştirilemez). ⚠ Ekstre almış kayıt silinemez; sunucu
 * `SNAK_TENANT_HAS_STATEMENTS` ile reddeder, yolu kapatmaktır.
 */
export function useDeleteSnakTenant() {
  const invalidate = useSnakInvalidator();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: true }>(`/integrations/snak/tenants/${id}`),
    onSuccess: invalidate,
  });
}

export function useRotateSnakSecret() {
  const invalidate = useSnakInvalidator();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ secret: string }>(`/integrations/snak/tenants/${id}/rotate-secret`),
    onSuccess: invalidate,
  });
}

export function useAddSnakTerms() {
  const invalidate = useSnakInvalidator();
  return useMutation({
    mutationFn: (input: {
      tenantId: string;
      effectiveFrom: string;
      billingMode: SnakBillingMode;
      monthlyFeeNet: string;
      commissionPercent: string;
      vatMode: SnakVatMode;
      listPriceNet?: string | null;
    }) => {
      const { tenantId, ...body } = input;
      return api.post<SnakTerms>(`/integrations/snak/tenants/${tenantId}/terms`, body);
    },
    onSuccess: invalidate,
  });
}

export function useRetrySnakStatement() {
  const invalidate = useSnakInvalidator();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<SnakStatementRow>(`/integrations/snak/statements/${id}/retry`),
    onSuccess: invalidate,
  });
}

export function useRunSnakNow() {
  const invalidate = useSnakInvalidator();
  return useMutation({
    mutationFn: () =>
      api.post<{ busy: boolean; reevaluated?: number; invoiced?: number; redelivered?: number }>(
        '/integrations/snak/run',
      ),
    onSuccess: invalidate,
  });
}
